import React from 'react';
import type { InputHTMLAttributes } from 'react';
import styles from './Input.module.scss';
import classNames from 'classnames';
import type { Merge } from 'types/utility';

type InputProps = Omit<React.HTMLProps<HTMLInputElement>, 'ref'>;

interface OwnProps {
  ref?: React.Ref<HTMLInputElement>;
  size?: 'h2' | 'small' | 'medium' | 'large';
  hideFromFullstory?: boolean | null | undefined;
  selectOnFocus?: boolean;
  onFocus?: InputProps['onFocus'];
  variant?: 'default' | 'asText' | 'asSearch' | 'asTransparent';
  ['data-testid']?: string;
  hasValidationError?: boolean;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
}

export type Props = Merge<InputProps, OwnProps>;

const Input = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    variant,
    className,
    value,
    size,
    hideFromFullstory,
    onFocus,
    type,
    selectOnFocus,
    hasValidationError,
    autoComplete,
    'data-testid': dataTestId,
    ...rest
  } = props;

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (selectOnFocus && event.target) {
      event.target.select();
    }
    if (onFocus) {
      onFocus(event);
    }
  };

  const containerClass = classNames(
    styles.container,
    styles[size || 'medium'],
    styles[variant || 'default'],
    {
      'fs-mask': hideFromFullstory,
    },
    className,
    {
      [styles.hasValidationError]: hasValidationError,
    }
  );
  const extraAttrs =
    type === 'number'
      ? {
          step: 'any', // works around numeric validation errors in Safari
        }
      : undefined;

  return (
    <input
      data-testid={dataTestId || 'input_field'}
      ref={ref}
      type={type || 'text'}
      {...extraAttrs}
      {...rest}
      onFocus={selectOnFocus ? handleFocus : onFocus}
      value={value}
      className={containerClass}
      autoComplete={autoComplete}
    />
  );
});

Input.displayName = 'Input';

export default Input;
